import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Grid,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import { FileDownload as DownloadIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCashOut } from "@/hooks/useCashOut";
import { useCashOutV2 } from "@/hooks/useCashOutV2";
import { usePermissions } from "@/hooks/usePermissions";
import { VENUES, type Venue } from "@restaurant-staff-portal/shared";
import { toast } from "react-hot-toast";

export function CashOutReportsPage() {
  const { cashOutPermissions } = usePermissions();
  const { exportCashOuts: exportV1 } = useCashOut();
  const { exportCashOuts: exportV2 } = useCashOutV2();

  const [filters, setFilters] = useState({
    venue: "" as Venue | "",
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  const [isExportingV1, setIsExportingV1] = useState(false);
  const [isExportingV2, setIsExportingV2] = useState(false);

  const handleExportV1 = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error("Please select start and end dates for export");
      return;
    }

    setIsExportingV1(true);
    try {
      await exportV1({
        venue: filters.venue || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: "csv",
      });
    } finally {
      setIsExportingV1(false);
    }
  };

  const handleExportV2 = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error("Please select start and end dates for export");
      return;
    }

    setIsExportingV2(true);
    try {
      await exportV2({
        venue: filters.venue || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: "csv",
      });
    } finally {
      setIsExportingV2(false);
    }
  };

  if (!cashOutPermissions.canExportCSV) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          You do not have permission to export cash out reports.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Cash Out Reports
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Export cash out data in CSV format. Choose between the legacy format
          (V1 - Revel/Moneris) or the new format (V2 - Toast POS).
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Filters
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Venue"
                  value={filters.venue}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      venue: e.target.value as Venue | "",
                    })
                  }
                >
                  <MenuItem value="">All Venues</MenuItem>
                  {Object.entries(VENUES).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) =>
                    setFilters({ ...filters, startDate: date })
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => setFilters({ ...filters, endDate: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>

          {(!filters.startDate || !filters.endDate) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please select both start and end dates to enable export.
            </Alert>
          )}
        </Paper>

        {/* Export Options */}
        <Grid container spacing={3}>
          {/* V2 Export (Toast POS) - Primary */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" gutterBottom color="primary">
                V2 - Toast POS Format
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export cash outs in the new Toast POS format. Includes:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 3, pl: 2 }}>
                <li>Toast Sales Data</li>
                <li>Staff/House Accounts with Tips & Gratuity</li>
                <li>Cash, Credit, Gift Card Payment Centres</li>
                <li>Sales & Tips Reconciliation</li>
                <li>House Calculations</li>
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleExportV2}
                disabled={
                  !filters.startDate || !filters.endDate || isExportingV2
                }
                fullWidth
              >
                {isExportingV2 ? "Exporting..." : "Export V2 (Toast)"}
              </Button>
            </Paper>
          </Grid>

          {/* V1 Export (Revel/Moneris) - Legacy */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: "100%", bgcolor: "grey.50" }}>
              <Typography variant="h6" gutterBottom>
                V1 - Legacy Format
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export cash outs in the legacy Revel/Moneris format. Includes:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 3, pl: 2 }}>
                <li>Revel Sales Data</li>
                <li>Staff/House Accounts</li>
                <li>Revel POS Payments</li>
                <li>Moneris Terminal Data</li>
                <li>Legacy Reconciliation</li>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportV1}
                disabled={
                  !filters.startDate || !filters.endDate || isExportingV1
                }
                fullWidth
              >
                {isExportingV1 ? "Exporting..." : "Export V1 (Legacy)"}
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Note on Data Formats
          </Typography>
          <Typography variant="body2">
            V1 and V2 contain different data structures and are not
            interchangeable. V2 (Toast POS) is the current active system. V1
            (Revel/Moneris) is maintained for historical data access only.
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
}
